import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, FileText, Loader2, FileWarning } from 'lucide-react';
import api from '@/lib/api';

function detectKind(contentType, filename) {
  const ct = (contentType || '').toLowerCase();
  if (ct.startsWith('image/')) return 'image';
  if (ct.includes('pdf')) return 'pdf';
  // FileResponse forwards the stored content_type, but fall back to extension.
  const src = `${filename || ''}`.toLowerCase();
  if (/\.pdf(\?|#|$|\s)/.test(src)) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp)(\?|#|$|\s)/.test(src)) return 'image';
  return 'other';
}

// Section D: in-portal file preview modal. Fetches the authenticated preview
// endpoint as a blob and renders PDFs/images inline, with a fallback otherwise.
export function DocumentPreviewModal({ open, onOpenChange, doc }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [objectUrl, setObjectUrl] = useState('');
  const [kind, setKind] = useState('other');

  useEffect(() => {
    let cancelled = false;
    let createdUrl = '';
    if (open && doc?.id) {
      if (!doc.storage_path) {
        setError('nofile'); setObjectUrl(''); setKind('other'); setLoading(false);
      } else {
        setLoading(true); setError(''); setObjectUrl('');
        api.get(`/documents/${doc.id}/preview`, { responseType: 'blob' })
          .then(r => {
            if (cancelled) return;
            const ct = r.headers['content-type'] || r.data.type || doc.content_type || '';
            createdUrl = URL.createObjectURL(r.data);
            setObjectUrl(createdUrl);
            setKind(detectKind(ct, doc.original_filename));
          })
          .catch(() => { if (!cancelled) setError('fetch'); })
          .finally(() => { if (!cancelled) setLoading(false); });
      }
    }
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [open, doc]);

  const openInNewTab = () => {
    if (objectUrl) window.open(objectUrl, '_blank');
  };
  const download = () => {
    if (!objectUrl) return;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = doc?.original_filename || doc?.name || 'file';
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-4xl" data-testid="document-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-base truncate pr-6">{doc?.name || 'Preview'}</DialogTitle>
          <DialogDescription className="sr-only">File preview for {doc?.name || 'document'}</DialogDescription>
        </DialogHeader>
        <div className="min-h-[320px] flex items-center justify-center bg-background rounded border border-border overflow-hidden">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid="preview-loading" />}
          {!loading && error === 'nofile' && (
            <div className="text-center py-10" data-testid="preview-nofile">
              <FileWarning className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No file attached to this document.</p>
            </div>
          )}
          {!loading && error === 'fetch' && (
            <div className="text-center py-10" data-testid="preview-error">
              <FileWarning className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Could not load file preview.</p>
            </div>
          )}
          {!loading && !error && kind === 'image' && (
            <img src={objectUrl} alt={doc?.name} className="max-h-[70vh] max-w-full object-contain" data-testid="preview-image" />
          )}
          {!loading && !error && kind === 'pdf' && (
            <iframe src={objectUrl} title={doc?.name} className="w-full h-[70vh]" data-testid="preview-pdf" />
          )}
          {!loading && !error && kind === 'other' && (
            <div className="text-center py-10" data-testid="preview-fallback">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium truncate max-w-xs mx-auto">{doc?.name || 'File'}</p>
              <p className="text-xs text-muted-foreground mt-1">Preview not available for this file type.</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" className="border-border" onClick={openInNewTab} data-testid="preview-open-newtab">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in new tab
          </Button>
          <Button size="sm" className="bg-primary text-primary-foreground" onClick={download} data-testid="preview-download">
            <Download className="h-3.5 w-3.5 mr-1" /> Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPreviewModal;
